import { useState, type FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { OAuthConsent } from './OAuthConsent'
import { parseAuthorizeParams } from '../api/oauth'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { ErrorBanner } from '../components/ErrorBanner'
import { VerifyCodeForm } from '../components/VerifyCodeForm'
import { PasswordResetForm } from '../components/PasswordResetForm'

// Login renders the signin surface. Three flows live side by side:
// Google + Microsoft OIDC popups (one click each), and email/password
// with a register toggle for first-time setup and a forgot-password
// step (an emailed code, then a new password). Magic-link signin is the
// glow end-user pattern (auth-api), not the console pattern — console
// owners use Identity Platform directly.
//
// MCP-facing OAuth (CLAUDE.md §6) lands here too: when the server's
// /oauth/authorize handler bounces an unauthed browser through with
// ?return_to=/oauth/authorize?...&client_name=..., signin proceeds as
// usual but the post-signin nav flips into the consent panel instead of
// going to `from`.
export function Login() {
  const auth = useAuth()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const search = new URLSearchParams(location.search)
  const oauthParams = parseAuthorizeParams(search.get('return_to') ?? '')
  const clientName = search.get('client_name') ?? ''

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<'signin' | 'register' | 'verify' | 'forgot'>('signin')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // While a just-registered owner still needs to verify, stay on this card so
  // the inline code step is reachable instead of bouncing to the dashboard (R2).
  // Covers the 'verify' step AND the brief race after register where auth.user
  // is set (createUser signs in) but mode hasn't flipped yet. The redirect fires
  // automatically once confirmVerifyCode flips emailVerified → true.
  const needsVerify =
    (mode === 'verify' || mode === 'register') && auth.user !== null && !auth.user.emailVerified
  if (auth.user && !needsVerify) {
    if (oauthParams) {
      return <OAuthConsent params={oauthParams} clientName={clientName} />
    }
    return <Navigate to={from} replace />
  }

  const verifyEmailLabel = email || auth.user?.email || 'your email'

  const run = async (fn: () => Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      setError(toMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const handleEmail = (e: FormEvent) => {
    e.preventDefault()
    if (mode === 'signin') {
      run(() => auth.signInEmail(email, password))
      return
    }
    // Register: create the account, then advance to the inline verify step
    // (the redirect guard above keeps us on this card while unverified).
    run(async () => {
      await auth.signUpEmail(email, password)
      setMode('verify')
    })
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <img src="/backlit-lockup.png" alt="Backlit" className="mb-5 h-7 w-auto" />
        <h1 className="mb-1 font-serif text-3xl italic text-cream">Console</h1>
        <p className="mb-6 text-sm text-muted">
          {mode === 'verify'
            ? `Enter the 6-digit code we sent to ${verifyEmailLabel}.`
            : mode === 'forgot'
              ? 'Reset your password.'
              : oauthParams
              ? `Sign in to connect ${clientName.trim() || 'an external application'} to your Backlit account.`
              : mode === 'signin'
                ? 'Sign in to manage your glows.'
                : 'Create an account to start deploying glows.'}
        </p>

        {mode === 'verify' ? (
          <VerifyCodeForm
            autoSend
            changeEmail={
              <button
                type="button"
                className="font-medium text-glow-600 hover:underline"
                onClick={() => {
                  // Wrong email: drop the just-created unverified account and
                  // go back to the register form.
                  void auth.signOut().then(() => setMode('register'))
                }}
              >
                Change email
              </button>
            }
          />
        ) : mode === 'forgot' ? (
          <PasswordResetForm initialEmail={email} onBack={() => setMode('signin')} />
        ) : (
          <>
        {error && <ErrorBanner className="mb-4">{error}</ErrorBanner>}

        <div className="space-y-2">
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={busy}
            onClick={() => run(() => auth.signInGoogle())}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={busy}
            onClick={() => run(() => auth.signInMicrosoft())}
          >
            Continue with Microsoft
          </Button>
        </div>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted">
          <div className="h-px flex-1 bg-surface-2" />
          or
          <div className="h-px flex-1 bg-surface-2" />
        </div>

        <form className="space-y-3" onSubmit={handleEmail}>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            minLength={mode === 'register' ? 8 : undefined}
          />
          {mode === 'signin' && (
            <div className="text-right text-sm">
              <button
                type="button"
                className="font-medium text-glow-600 hover:underline"
                onClick={() => {
                  setError(null)
                  setMode('forgot')
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={busy}>
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-cream/70">
          {mode === 'signin' ? (
            <>
              No account?{' '}
              <button
                type="button"
                className="font-medium text-glow-600 hover:underline"
                onClick={() => setMode('register')}
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                className="font-medium text-glow-600 hover:underline"
                onClick={() => setMode('signin')}
              >
                Sign in
              </button>
            </>
          )}
        </p>
          </>
        )}
      </Card>
    </div>
  )
}

// toMessage turns whatever Firebase throws into something the user can
// read. We don't try to map every Firebase error code here; the common
// cases get specific messages and the rest fall back to the raw message.
function toMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'code' in e) {
    const code = (e as { code: unknown }).code
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
      return 'Incorrect email or password.'
    }
    if (code === 'auth/email-already-in-use') {
      return 'That email is already registered. Try signing in instead.'
    }
    if (code === 'auth/weak-password') {
      return 'Password must be at least 8 characters.'
    }
    if (code === 'auth/popup-closed-by-user') {
      return 'Signin cancelled.'
    }
  }
  if (e instanceof Error) return e.message
  return 'Something went wrong. Please try again.'
}
